import { FormInputGroup, FormRow } from 'framework/ui/layout';
import { useFormContext, TextInput, EnumDropdown, FormFieldsData, ClearAfterUnmountWrapper } from 'framework/form';
import {
  ENGAGEMENT_TYPE_OPTIONS,
  PROJECT_SCENARIO_HINTS,
  PROJECT_SCENARIO_OPTIONS,
  ProjectSearchDto,
} from 'domain/shared/newProject';
import { useEffect } from 'react';
import { get, merge } from 'lodash';
import { Box, Stack, Typography } from '@mui/material';
import { Alert, Tooltip } from 'framework/ui/mui-builder';
import { GenerateCodeNameButton, useCodeNameService } from 'domain/shared/core/codename';
import RelatedProjectsByAccountDropdown from './step1/RelatedProjectsByAccountDropdown';
import ApprovalRequiredToggler from '../../shared/mainProjectSection/ApprovalRequiredToggler';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const getProjectBudgetId = (project?: { defaultBudgetId?: StringOrNumber | null; availableBudgets?: unknown[] }) => {
  if (project?.defaultBudgetId) {
    return project?.defaultBudgetId;
  }

  const firstAvailableBudget = project?.availableBudgets?.[0];
  if (!firstAvailableBudget) {
    return null;
  }

  return get(firstAvailableBudget, 'id') || get(firstAvailableBudget, 'value') || null;
};

const ProjectConfigurationForm = ({
  source,
  accountIdSource,
  onScenarioChange,
  onRelatedProjectChange = () => Promise.resolve({}),
}: {
  source: string;
  accountIdSource: string;
  onScenarioChange?: (scenario: keyof typeof PROJECT_SCENARIO_OPTIONS) => void | Promise<void>;
  onRelatedProjectChange?: (
    project: ProjectSearchDto,
    defaultBudgetId?: Nullable<StringOrNumber>,
  ) => Promise<FormFieldsData>;
}) => {
  const { data = {}, onChangeCallback } = useFormContext();

  const { generateCodeName } = useCodeNameService();
  const scenarioTooltip = (
    <Stack spacing={0.75}>
      {Object.entries(PROJECT_SCENARIO_OPTIONS).map(([scenarioKey, label]) => (
        <Stack key={scenarioKey} spacing={0.25}>
          <Typography variant="subtitle2">{label}</Typography>
          <Typography variant="body2">
            {PROJECT_SCENARIO_HINTS[scenarioKey as keyof typeof PROJECT_SCENARIO_HINTS]}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  useEffect(() => {
    const projectCodeName = get(data, `${source}.projectCodeName`);
    if (!projectCodeName) {
      generateCodeName(projectCodeName).then(response => {
        onChangeCallback?.({ [`${source}.projectCodeName`]: response.name });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack>
      <Typography color="textSecondary" variant="body2" pb={2}>
        The system has pre-selected a scenario based on available data. Please review it and adjust if needed. <br />
        Your selection will determine how the project is configured.
      </Typography>

      <FormInputGroup>
        <FormRow colXl={3} colMd={3}>
          <>
            <EnumDropdown
              source={`${source}.scenario`}
              options={PROJECT_SCENARIO_OPTIONS}
              onChangeCallback={values => {
                const scenario = values?.[`${source}.scenario`] as keyof typeof PROJECT_SCENARIO_OPTIONS | undefined;

                if (!scenario) {
                  return;
                }

                onScenarioChange?.(scenario);
              }}
              tooltip={scenarioTooltip}
              label="Scenario"
            />
            <FormInputGroup isVisible={formData => get(formData, `${source}.createDaVinciRedmine`) === true}>
              <EnumDropdown
                source={`${source}.engagementType`}
                options={ENGAGEMENT_TYPE_OPTIONS}
                placeholder="Select engagement type"
              />
            </FormInputGroup>
            <FormInputGroup
              isVisible={formData =>
                get(formData, `${source}.createDaVinciRedmine`) === false ||
                get(formData, `${source}.createNewProject`) === false
              }
            >
              <RelatedProjectsByAccountDropdown
                source={`${source}.existingProject.id`}
                accountIdSource={accountIdSource}
                label="Related DaVinci project"
                additionalChangeDeps={[data?.project?.scenario]}
                onAdditionalChangeCallback={async project => {
                  const defaultBudgetId = getProjectBudgetId(project);
                  const updatedProjectFormData = project ? await onRelatedProjectChange(project, defaultBudgetId) : {};
                  const baseData = {
                    [`${source}.existingProject.name`]: project?.name,
                    [`${source}.existingProject.sowId`]: project?.sowId,
                    [`${source}.existingProject.defaultBudgetId`]: project?.defaultBudgetId,
                  };

                  return merge(baseData, updatedProjectFormData);
                }}
              />
            </FormInputGroup>
            {data?.project?.createDaVinciRedmine === false && (
              <Alert severity="warning">Information for selected project will be rewritten</Alert>
            )}
          </>
        </FormRow>
        <FormInputGroup
          isVisible={formData =>
            get(formData, `${source}.createNewProject`) === true &&
            get(formData, `${source}.createDaVinciRedmine`) === true
          }
        >
          <FormRow colXl={3} colMd={3}>
            <TextInput source={`${source}.originalName`} label="Project original name" />
            <TextInput source={`${source}.projectCodeName`} label="Project code name" />
            <GenerateCodeNameButton
              source={`${source}.projectCodeName`}
              setCodeNameMethod={name => {
                onChangeCallback?.({ [`${source}.projectCodeName`]: name });
              }}
              isVisible
              color="primary"
            />
          </FormRow>
          <FormRow colXl={6} colMd={6} pl={1}>
            <ClearAfterUnmountWrapper>
              <ApprovalRequiredToggler
                source={`${source}.isProjectForecastTrackingEnabled`}
                label={
                  <Box display={'flex'} alignItems={'center'}>
                    <Typography>Enable Project Forecast Tracking</Typography>
                    <Tooltip
                      title="Defines whether the system will track delivery forecasts (progress, budget, timeline) for the project.
"
                    >
                      <ErrorOutlineIcon sx={{ color: 'text.secondary', pl: 1, size: 'small' }} />
                    </Tooltip>
                  </Box>
                }
              />
            </ClearAfterUnmountWrapper>
          </FormRow>
          <FormRow colXl={6} colMd={6} pl={1}>
            <ClearAfterUnmountWrapper
              isVisible={formData => get(formData, `${source}.isProjectForecastTrackingEnabled`) === true}
            >
              <ApprovalRequiredToggler
                source={`${source}.isEvmTrackingEnabled`}
                label={
                  <Box display={'flex'} alignItems={'center'}>
                    <Typography>Enable EVM Tracking</Typography>
                    <Tooltip title="Enables advanced Earned Value Management tracking for the project">
                      <ErrorOutlineIcon fontSize="small" sx={{ color: 'text.secondary', pl: 1 }} />
                    </Tooltip>
                  </Box>
                }
              />
            </ClearAfterUnmountWrapper>
          </FormRow>
        </FormInputGroup>
      </FormInputGroup>
    </Stack>
  );
};

export default ProjectConfigurationForm;
